import { getExtension, isImageAttachment } from '@modules/tarefas/utils/attachments'

/**
 * [spec-003 T301 / US-4, US-5, US-6] O que a análise produziu: planilha,
 * documento ou gráfico.
 *
 * ⚠️ NÃO confundir com o `tipo` do evento `arquivo`
 * (`spec-002/contracts/events.schema.json`, `$defs.d_arquivo`): aquele é
 * `anexo | artefato | manifesto` e diz de ONDE o arquivo veio. Este eixo diz o
 * QUE ele é, e é o que separa as três user stories — a planilha que se baixa
 * (US-4), o documento que vai para a ata (US-6) e o gráfico que aparece na
 * conversa (US-5).
 *
 * A régua de imagem é a de `@modules/tarefas/utils/attachments` — a MESMA que
 * o `composer-attachments` já usa para decidir miniatura. Uma segunda
 * definição de "isto é imagem" na mesma conversa divergiria na primeira
 * extensão nova.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
export const TIPO_DE_ARTEFATO = Object.freeze({
  PLANILHA: 'planilha',
  DOCUMENTO: 'documento',
  GRAFICO: 'grafico',
  // O que não é nenhum dos três. Existe para a tela nunca ficar sem rótulo —
  // arquivo sem tipo reconhecido continua sendo arquivo entregue.
  ARQUIVO: 'arquivo',
})

const CONTENT_TYPES_DE_PLANILHA = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
]

const CONTENT_TYPES_DE_DOCUMENTO = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
]

const EXTENSOES_DE_PLANILHA = ['xlsx', 'xls', 'csv']
const EXTENSOES_DE_DOCUMENTO = ['pdf', 'docx', 'doc', 'pptx', 'ppt']

/**
 * O tipo de UM artefato.
 *
 * Aceita os dois shapes que circulam no módulo: o do evento `arquivo`
 * (`{content_type, nome}`) e o da ENTREGA já adaptada para o composer
 * (`{type, name}`, de `utils/artefato-para-item.js`) — os dois descrevem o
 * mesmo objeto, e exigir um só espalharia tradução pelos chamadores.
 *
 * ★ O `content_type` vence o nome: ele é o que o produtor DECLAROU; a extensão
 * é o que alguém digitou. Mas o nome é a segunda fonte de verdade, e não
 * decoração — o campo é `type: string` livre no contrato, e artefato sem ele
 * cairia todo no genérico.
 *
 * @param {Object} artefato `{content_type|type, nome|name}`.
 * @returns {String} Um valor de `TIPO_DE_ARTEFATO`.
 */
export function tipoDeArtefato(artefato) {
  const alvo = artefato || {}
  const contentType = String(alvo.content_type || alvo.type || '').toLowerCase()
  const nome = alvo.nome || alvo.name || ''

  if (contentType) {
    if (CONTENT_TYPES_DE_PLANILHA.indexOf(contentType) !== -1) return TIPO_DE_ARTEFATO.PLANILHA
    if (CONTENT_TYPES_DE_DOCUMENTO.indexOf(contentType) !== -1) return TIPO_DE_ARTEFATO.DOCUMENTO
    if (isImageAttachment({ mimeType: contentType })) return TIPO_DE_ARTEFATO.GRAFICO
  }

  const extensao = getExtension(nome)
  if (EXTENSOES_DE_PLANILHA.indexOf(extensao) !== -1) return TIPO_DE_ARTEFATO.PLANILHA
  if (EXTENSOES_DE_DOCUMENTO.indexOf(extensao) !== -1) return TIPO_DE_ARTEFATO.DOCUMENTO
  if (isImageAttachment({ fileName: nome })) return TIPO_DE_ARTEFATO.GRAFICO

  return TIPO_DE_ARTEFATO.ARQUIVO
}

/**
 * Este artefato é o gráfico da US-5?
 *
 * @param {Object} artefato O evento `arquivo` ou a entrega adaptada.
 * @returns {Boolean}
 */
export function ehGrafico(artefato) {
  return tipoDeArtefato(artefato) === TIPO_DE_ARTEFATO.GRAFICO
}

/**
 * Os gráficos entre as ENTREGAS do turno — os únicos que o thread desenha
 * inline (US-5).
 *
 * ★ Entrega sem `id_objeto` fica de fora: é ele que `apiArtefatos.baixar` pede
 * ao servidor (`{id_conversa, id_objeto}`), e uma moldura de imagem que nunca
 * carrega é pior que imagem nenhuma — ela promete um gráfico que não vem.
 *
 * @param {Array<Object>} entregas A saída de `entregasDoTurno`.
 * @returns {Array<Object>} Só os gráficos com como ser buscados.
 */
export function graficosEntreEntregas(entregas) {
  return (entregas || []).filter(function (entrega) {
    return Boolean(entrega && entrega.id_objeto && ehGrafico(entrega))
  })
}
